import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CreditCard, ShieldCheck, Save, Loader2, CheckCircle, AlertTriangle, Download, MailWarning, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * HelperPayoutDetails — dropped into HelperDashboard.
 *
 * Self-contained: fetches its own copy of the helper's payout details on
 * mount so we don't have to thread state up through the parent. Saves to
 * PUT /api/helper/payout-details using the helper_token from localStorage.
 *
 * Visible only to the logged-in helper (this dashboard page) and to admins
 * via /admin/referrals — never returned from any public endpoint.
 */
const HelperPayoutDetails = () => {
  const { t } = useTranslation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('helper_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [form, setForm] = useState({
    method: 'sepa',
    accountHolder: '',
    iban: '',
    bic: '',
    bankName: '',
    country: 'ES',
    paypalEmail: '',
    notes: '',
    legalName: '',
    taxId: '',
    taxCountry: 'ES',
    entityType: 'individual',
    currentPassword: '',
  });
  const [originalUpdatedAt, setOriginalUpdatedAt] = useState(null);
  const [security, setSecurity] = useState({ emailVerified: true, payoutHoldActive: false, payoutHoldUntil: null, clawbackOutstanding: 0 });
  const [resending, setResending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/helper/payout-details`, { headers });
        if (cancel) return;
        const d = res.data?.payoutDetails || {};
        setForm(f => ({
          ...f,
          method: d.method || 'sepa',
          accountHolder: d.accountHolder || '',
          iban: d.iban || '',
          bic: d.bic || '',
          bankName: d.bankName || '',
          country: d.country || 'ES',
          paypalEmail: d.paypalEmail || '',
          notes: d.notes || '',
          legalName: d.legalName || '',
          taxId: d.taxId || '',
          taxCountry: d.taxCountry || 'ES',
          entityType: d.entityType || 'individual',
        }));
        setOriginalUpdatedAt(d.updatedAt || null);
        setSecurity({
          emailVerified: res.data?.emailVerified !== false,
          payoutHoldActive: !!res.data?.payoutHoldActive,
          payoutHoldUntil: res.data?.payoutHoldUntil || null,
          clawbackOutstanding: res.data?.clawbackOutstanding || 0,
        });
      } catch (e) {
        // Non-fatal — just leave the empty form.
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const isSepa = form.method === 'sepa';
  const isWise = form.method === 'wise';
  const isPaypal = form.method === 'paypal';

  const downloadStatement = async (kind) => {
    try {
      const res = await axios.get(`${API}/helper/statement/${kind}.csv`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `find2day-${kind}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || e.message });
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/helper/resend-verification`, {}, { headers });
      setMessage({ type: 'success', text: t('verifyResent', 'Verification email sent — check your inbox.') });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || e.message });
    } finally {
      setResending(false);
    }
  };

  const validate = () => {
    if (!form.accountHolder.trim()) return t('payoutNeedHolder', 'Account holder name is required.');
    if (isSepa || isWise) {
      const iban = form.iban.replace(/\s+/g, '');
      if (!iban) return t('payoutNeedIban', 'IBAN is required.');
      if (iban.length < 15 || iban.length > 34) return t('payoutBadIban', 'IBAN length looks wrong (15–34 characters).');
    }
    if (isPaypal && !form.paypalEmail.trim()) return t('payoutNeedPaypal', 'PayPal email is required.');
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const err = validate();
    if (err) { setMessage({ type: 'error', text: err }); return; }
    setSaving(true);
    try {
      const payload = {
        method: form.method,
        accountHolder: form.accountHolder,
        country: form.country,
        notes: form.notes,
        legalName: form.legalName,
        taxId: form.taxId,
        taxCountry: form.taxCountry,
        entityType: form.entityType,
        currentPassword: form.currentPassword,
      };
      if (isSepa || isWise) {
        payload.iban = form.iban;
        payload.bic = form.bic;
        payload.bankName = form.bankName;
      }
      if (isPaypal) payload.paypalEmail = form.paypalEmail;
      const res = await axios.put(`${API}/helper/payout-details`, payload, { headers });
      setOriginalUpdatedAt(res.data?.payoutDetails?.updatedAt || new Date().toISOString());
      setForm(f => ({ ...f, currentPassword: '' }));
      if (res.data?.payoutHoldUntil) {
        setSecurity(s => ({ ...s, payoutHoldActive: true, payoutHoldUntil: res.data.payoutHoldUntil }));
      }
      setMessage({ type: 'success', text: t('payoutSaved', 'Payout details saved.') });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5" data-testid="helper-payout-details">
      <div className="flex items-start gap-3 mb-3">
        <span className="inline-flex w-9 h-9 rounded-lg bg-purple-100 text-purple-700 items-center justify-center flex-shrink-0">
          <CreditCard className="h-4 w-4" />
        </span>
        <div>
          <h3 className="font-semibold text-gray-900">{t('payoutDetailsTitle', 'Payout details')}</h3>
          <p className="text-xs text-gray-500">{t('payoutDetailsHint', 'Where we send your commission. Only you and the Find-2-Day admin can see these details.')}</p>
        </div>
      </div>

      {!security.emailVerified && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="verify-email-banner">
          <MailWarning className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            {t('verifyEmailWarn', 'Please verify your email to enable commission payouts.')}
            <button type="button" onClick={resendVerification} disabled={resending}
              className="ml-2 underline font-semibold disabled:opacity-50" data-testid="resend-verification-btn">
              {resending ? t('sending', 'Sending…') : t('resendVerification', 'Resend email')}
            </button>
          </div>
        </div>
      )}
      {security.payoutHoldActive && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="payout-hold-banner">
          <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{t('payoutHoldWarn', 'Your bank details changed recently, so payouts are briefly on hold for security.')}</span>
        </div>
      )}
      {security.clawbackOutstanding > 0 && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="clawback-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{t('clawbackWarn', 'An outstanding balance of €{{amt}} from a refund/chargeback will be deducted from future commissions.', { amt: security.clawbackOutstanding.toFixed(2) })}</span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500 py-4 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> {t('loading', 'Loading…')}</div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('payoutMethod', 'Payment method')}</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'sepa', label: 'SEPA' },
                { v: 'wise', label: 'Wise' },
                { v: 'paypal', label: 'PayPal' },
              ].map(opt => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setForm(f => ({ ...f, method: opt.v }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${form.method === opt.v ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`payout-method-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('accountHolder', 'Account holder name')} *</label>
            <input type="text" value={form.accountHolder} onChange={set('accountHolder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              data-testid="payout-holder-input" autoComplete="name" />
          </div>

          {(isSepa || isWise) && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">IBAN *</label>
                <input type="text" value={form.iban} onChange={set('iban')}
                  placeholder="ES91 2100 0418 4502 0005 1332"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="payout-iban-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">BIC / SWIFT</label>
                  <input type="text" value={form.bic} onChange={set('bic')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="payout-bic-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('bankName', 'Bank name')}</label>
                  <input type="text" value={form.bankName} onChange={set('bankName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="payout-bankname-input" />
                </div>
              </div>
            </>
          )}

          {isPaypal && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('paypalEmail', 'PayPal email')} *</label>
              <input type="email" value={form.paypalEmail} onChange={set('paypalEmail')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="payout-paypal-input" autoComplete="email" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('country', 'Country')} (ISO-2)</label>
              <input type="text" maxLength={2} value={form.country} onChange={(e) => setForm(f => ({ ...f, country: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="payout-country-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('paymentReference', 'Payment reference / notes')}</label>
              <input type="text" value={form.notes} onChange={set('notes')}
                placeholder="Find-2-Day commissions"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="payout-notes-input" />
            </div>
          </div>

          {/* Tax details (for compliant statements / invoicing) */}
          <div className="pt-1 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-2 mt-2">{t('taxDetailsTitle', 'Tax details (for your statements)')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('legalName', 'Legal name')}</label>
                <input type="text" value={form.legalName} onChange={set('legalName')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="payout-legalname-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('taxId', 'Tax ID / NIF / VAT')}</label>
                <input type="text" value={form.taxId} onChange={set('taxId')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="payout-taxid-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('taxCountry', 'Tax country')} (ISO-2)</label>
                <input type="text" maxLength={2} value={form.taxCountry} onChange={(e) => setForm(f => ({ ...f, taxCountry: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="payout-taxcountry-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('entityType', 'You are a')}</label>
                <select value={form.entityType} onChange={set('entityType')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="payout-entitytype-select">
                  <option value="individual">{t('entityIndividual', 'Individual')}</option>
                  <option value="company">{t('entityCompany', 'Company / self-employed')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Current password — required to change bank details */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> {t('currentPasswordLabel', 'Current password (required to change bank / payout details)')}
            </label>
            <input type="password" value={form.currentPassword} onChange={set('currentPassword')}
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              data-testid="payout-current-password-input" />
          </div>

          {/* Downloadable statements */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={() => downloadStatement('earnings')}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
              data-testid="download-earnings-btn">
              <Download className="h-4 w-4" /> {t('downloadEarnings', 'Earnings statement (CSV)')}
            </button>
            <button type="button" onClick={() => downloadStatement('payouts')}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
              data-testid="download-payouts-btn">
              <Download className="h-4 w-4" /> {t('downloadPayouts', 'Payout statement (CSV)')}
            </button>
          </div>

          {message.text && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="payout-form-message">
              {message.type === 'error' ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
              {message.text}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <p className="text-xs text-gray-500 inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              {originalUpdatedAt ? (
                t('payoutLastSaved', 'Last saved {{date}}', { date: new Date(originalUpdatedAt).toLocaleString() })
              ) : (
                t('payoutNotYetSaved', 'Not saved yet — admin cannot pay you until this is filled in.')
              )}
            </p>
            <button type="submit" disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="payout-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('save', 'Save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default HelperPayoutDetails;
